import FormComponent from '../FormComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import { isEmpty } from 'lodash-es';
import { addInvalidClasses, removeInvalidClasses } from '../../utils/validateFormElement';

/* 	
	Example step config:
	{
		default: true,
		dependentQuestion: [2],
		errorMessage: 'Please upload a file to proceed',
		fieldName: 'user',
		acceptedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
		allowMultipleFileUpload: true,
		instructionText: 'Please upload a photo of yourself and any documentation related to your learning.',
		label: 'Upload your documentation',
		id: 1,
		options: [],
		question: 'any documentation related to your learning',
		questionPrefix: 'Please upload',
		questionType: 'fileUpload',
		buttonText: 'Browse files'
		step: 1,
		visible: true
	}
*/

const CHANGE_EVENT = 'change';

class FileUploadComponent extends FormComponent {
	constructor(containerElement, config) {
		super(containerElement, config);
		this.classPrefix = 'lta-form-fileupload';
		this.errorMessage = config.data.errorMessage || 'Please upload a file to proceed';
		this.initEventListeners(config.data);
		this.allFiles = [];
		this.allowMultiple = config.data.allowMultipleFileUpload;
		this.maxFiles = config.data.maxFiles ? config.data.maxFiles : 3; 
	}

	markup(value, data, titleMarkup) {
		const acceptedFiles = data.acceptedFileTypes.join(', ');
		return `
      <fieldset>
        <legend>${titleMarkup}</legend>
        <div class="lta-search-input-field u-mb-2">
          <div id="lta-fileupload-error"></div>
          <div>
            <div class="u-mb-4">
              <p class="u-text-grey-lta u-mb-1 lta-text-input__label">${data.label}</p>
              <p class="u-body2 u-text-grey-dark-slate u-mb-1">${data.instructionText}</p>
				<div class="lta-form-fileupload__container">
					<p id="lta-fileupload-selected-files" class="u-mb-3"><span>No file chosen</span></p>
					<input
						class="lta-form-fileupload__input"
						type="file" id="${data.id}"
						value="${data.value}"
						name="${data.id}"
						accept="${acceptedFiles}"
						${data.allowMultipleFileUpload ? 'multiple' : ''}
					/>
					<label class="lta-btn lta-btn--default lta-btn--secondary" for="${data.id}">${data.buttonText}</label>
				</div>
            </div>
          </div>
        </div>
      </fieldset>
    `;
	}

	initEventListeners() {
		const fileUploadElements = SelectorEngine.find('input[type="file"]', this.containerEl);

		// eslint-disable-next-line unicorn/no-array-for-each
		fileUploadElements.forEach((fileUploadElement) => {
			EventHandler.on(fileUploadElement, CHANGE_EVENT, () => {
				this.validateFile();
			});
		});
	}

	validateFile() {
		const fileUpload = SelectorEngine.find('input[type="file"]', this.containerEl);

		if (isEmpty(fileUpload[0].value) && isEmpty(this.allFiles)) {
			this.setError(this.errorMessage);
			return false;
		} else if (!isEmpty(fileUpload[0].value)) {
			const files = [...fileUpload[0].files];
			// Are any of the files over 5mb
			const isOversized = files.some((file) => file.size > 5_000_000);

			// Checking file format for validation
			const isAccepetdFiles = files.some((file) => this.data.acceptedFileTypes?.includes(file.type));

			if(!isAccepetdFiles) {
				const errorText = this.data.acceptedFileTypesError ? this.data.acceptedFileTypesError : 'Incorrect file format';
				this.setError(errorText);
				fileUpload[0].value = '';
				return false;
			}
			else if (isOversized) {
				const errorText = 'Your file exceeds the 5MB limit. Please reduce the size of your file and try again.';
				this.setError(errorText);
				fileUpload[0].value = '';
				return false;
			} else {
				this.clearError();
				this.updateValue(files);
				return true;
			}
		}
	}

	validate() {
		const fileUpload = SelectorEngine.find('input[type="file"]', this.containerEl);

		if (isEmpty(fileUpload[0].value) && isEmpty(this.allFiles)) {
			this.setError(this.errorMessage);
			return false;
		}
		if(this.allFiles.length > this.maxFiles) {
			this.setError(`The maximum number of files you can upload is ${this.maxFiles}. Please remove the extra files and try again`);
			return false;
		}
	}

	setError(errorMessage) {
		const errorElement = SelectorEngine.findOne('#lta-fileupload-error', this.containerEl);
		errorElement.textContent = errorMessage;
		addInvalidClasses(errorElement);
	}

	clearError() {
		const errorElement = SelectorEngine.findOne('#lta-fileupload-error', this.containerEl);
		errorElement.textContent = '';
		removeInvalidClasses(errorElement);
	}

	updateSelectedFilesDisplay() {
		const selectedFilesElement = SelectorEngine.findOne(
			'#lta-fileupload-selected-files',
			this.containerEl
		);

		if (this.allFiles.length === 0) {
			selectedFilesElement.innerHTML = '<span>No files selected</span>';
			return;
		}

		selectedFilesElement.innerHTML = this.allFiles
			.map((file, index) => `
				<div class="file-item">
					<span>${file.name} <button data-index="${index}" style="border: none !important;" type="button" class="remove-file-btn  u-p-05 lta-btn lta-btn--sm lta-btn--line lta-btn--icon lta-btn--icon-after"><svg class="fe-icon" width="1.5em" height="1.5em" fill="currentColor"><use xlink:href="/static/images/icons/lta-icons.svg#close"></use></svg></button></button></span>
				</div>
			`)
			.join('');

		// Adding click listeners to remove buttons
		selectedFilesElement.querySelectorAll('.remove-file-btn').forEach(btn => {
			btn.addEventListener('click', (e) => {
				const index = e.currentTarget.getAttribute('data-index');
				this.removeFile(index);
			});
		});
	}

	removeFile(index) {
		this.allFiles.splice(index, 1);
		this.value.value = this.allFiles;
		this.updateSelectedFilesDisplay();
		const input = SelectorEngine.findOne('input[type="file"]', this.containerEl);
		input.value = "";
		if(this.allFiles.length <= this.maxFiles) {
			this.clearError();
		}
	}

	updateValue(files) {
		const newFiles = Array.from(files);

		if (this.allowMultiple) {
			// merge files if allow multiple
			this.allFiles = this.allFiles.concat(newFiles);
		} else {
			// single file only replace
			this.allFiles = newFiles.slice(0, 1);
		}

		this.value = {
			id: this.data.id,
			fieldName: this.data.fieldName,
			value: this.allFiles
		};

		this.updateSelectedFilesDisplay();

		//NOTE: Currently only supporting one dependent question per fileUpload group
		this.dependentQuestion = this.data.dependentQuestion;
	}
}

export default FileUploadComponent;
